import * as com from "../../Common.js"

const occupancyToEngine = (occupancy) => {
    switch (occupancy) {
        case "secondhome":
            return "SECOND_HOME"
        case "investment":
            return "INVESTMENT"
        default:
            return "PRIMARY"
    }
}

const citizenshipToEngine = (person) => {
    const status = person?.citizenship
    if (status === "citizen") return ["US_CITIZENS", ""]
    if (status === "permanent") return ["PERMANENT_RESIDENT_ALIEN", ""]
    const visa = `${person?.visa || ""}`.replace("-", "_")
    return ["NON_PERMANENT_RESIDENT_ALIEN", visa]
}

export const getPrimaryParticipant = (application) => {
    if (!application) return null
    const id = application.primaryPartyId
    if (id && application.participantsById?.[id]) return application.participantsById[id]
    const order = application.participantOrder || Object.keys(application.participantsById || {})
    return application.participantsById?.[order[0]] || null
}

export const formatPrequalBorrowerName = (application) => {
    const person = getPrimaryParticipant(application)
    const name = [person?.firstName, person?.lastName].filter(Boolean).join(" ")
    return name || "-"
}

export const formatPrequalBorrowerEmail = (application) => {
    const person = getPrimaryParticipant(application)
    return `${person?.email || ""}`.toLowerCase()
}

const authHeaders = (borrowerId, extra = {}) => ({
    Authorization: "Bearer " + sessionStorage.getItem("ZeitroA"),
    Cache: "no-cache",
    "Content-Type": "application/json",
    "X-Borrower": borrowerId,
    ...extra,
})

const parseFees = (raw) => {
    if (!raw || raw.status === "Error" || raw.Status === "Error") return null
    return {
        cannotshopservices: raw.cannotshopservices,
        canshopservices: raw.canshopservices,
        closingcostsfinanced: raw.closingcostsfinanced,
        deposit: raw.deposit,
        downpayment: raw.downpayment,
        fundsforborrower: raw.fundsforborrower,
        initialescrowpaymentsatclosing: raw.initialescrowpaymentsatclosing,
        othercosts: raw.othercosts,
        prepaids: raw.prepaids,
        recordingandgovernmentfees: raw.recordingandgovernmentfees,
        transfertax: raw.transfertax,
        sellerscredits: raw.sellerscredits,
        adjustmentsothercredits: raw.adjustmentsothercredits,
        ...raw,
    }
}

const dscr = (person, monthlyDebt) => {
    const debt = com.safeParseInt(monthlyDebt)
    if (!debt) return 100
    return parseFloat((com.safeParseInt(person?.nonqm?.nonqmincomeamount) / debt).toFixed(2))
}

export const buildProductRequest = ({ application, loansize, fees, predti }) => {
    const property = application?.property || {}
    const person = getPrimaryParticipant(application)
    const [citizenship, visa] = citizenshipToEngine(person)
    const mortgageApplied = application?.product?.mortgageapplied
    const purpose = property.purpose || "purchase"
    const propertyValue = purpose === "purchase" ? property.salesprice : property.appraisal
    const propertyType = property.propertytype === "mobilehome" ? "manufacturedhome" : property.propertytype
    const monthlyDebt = predti?.dtiData?.[0]?.monthly_payment
    if (predti?.dtiData?.[0] && mortgageApplied === "nonqm") {
        let incomeamount = com.safeParseFloat(person?.nonqm?.nonqmincomeamount)
        if (person?.nonqm?.nonqmincomeperiod === "yearly") {
            incomeamount = Math.round(incomeamount / 12)
        }
        predti.dtiData[0].monthly_income = incomeamount
    }

    return {
        state: property.state,
        county: property.county,
        purpose: com.Purpose2Calculator(purpose),
        loanAmount: com.safeParseInt(loansize),
        appraisal: `${com.safeParseInt(propertyValue)}`,
        estimatedClosingCosts: fees || predti?.dtiData?.[0]?.fees || 0,
        firstTimeHomeBuyer: application?.firsttimebuyer,
        units: propertyType === "twotofourfamily" ? com.safeParseInt(property.numberofunits || property.units) : 1,
        occupancy: occupancyToEngine(property.occupancy),
        selfemployed: Boolean(person?.occupation?.selfemployed),
        property: com.getPropertyType(propertyType) || com.getPropertyType(property.propertytype),
        citizenship,
        visaType: visa,
        incomeDocumentationType: mortgageApplied === "nonqm" ? "" : "FullDoc",
        DSCR: dscr(person, monthlyDebt),
        preDTI: predti,
        nonQMDocTypes: person?.nonqm?.nonqmincometype,
    }
}

export const loadPrequalQuotes = async ({ loanId, borrowerId, application, loansize, fico }) => {
    const headers = authHeaders(borrowerId)
    let fees = null
    try {
        const feesRes = await fetch("/data/getFees", { method: "POST", headers })
        if (feesRes.ok) fees = parseFees(await feesRes.json())
    } catch (err) {
        console.error("prequal getFees:", err)
    }

    const dtiHeaders = authHeaders(borrowerId, { "X-Loan": loanId || "" })
    const dtiRes = await fetch("/data/getPreDTI?newSource=true", {
        method: "POST",
        headers: dtiHeaders,
    })
    if (!dtiRes.ok) {
        throw new Error("Failed to load pre-DTI")
    }
    const predti = await dtiRes.json()
    if (predti?.Error) {
        throw new Error(predti.Error)
    }
    if (fico) predti.fico = fico

    const request = buildProductRequest({ application, loansize, fees, predti })
    const productRes = await fetch("/data/getproducts", {
        method: "POST",
        headers,
        body: JSON.stringify(request),
    })
    if (!productRes.ok) {
        throw new Error("Failed to load products")
    }
    return productRes.json()
}
